import { z } from 'zod';

import { Iso8601 } from './primitives.js';

/**
 * Pilot (canary) targeting for a single launcher tile. When a tile carries
 * a `pilot` block AND the tenant is entitled to `pilotRollout`, the tile
 * renders ONLY on devices the rule selects — every other device never sees
 * it. The rule is evaluated client-side on each device (see
 * `runtime/pilot.ts`), so the per-tenant config stays single + ETag-cached.
 *
 * Lifecycle via the optional `from` / `to` window:
 *  - before `from`            → hidden everywhere (pilot not started)
 *  - within `[from, to-1day)` → the rule decides who sees it
 *  - at `to` minus one day    → GRADUATES to the whole fleet (a one-day
 *                               safety buffer to confirm 100% coverage
 *                               before the window closes)
 *  - `to` omitted             → ongoing segment, the rule applies forever
 *                               (the natural shape for subnet / tag pilots)
 */

/** An IPv4 CIDR like `10.2.0.0/16`. The Builder's subnet picker emits /8,
 *  /16, /24 (1st/2nd/3rd octet), but any 0–32 prefix validates. */
const Cidr = z
  .string()
  .regex(
    /^((25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(25[0-5]|2[0-4]\d|1?\d?\d)\/(3[0-2]|[12]?\d)$/,
    'must be an IPv4 CIDR (e.g. 10.2.0.0/16)',
  );

/** Static share of the fleet, in 5% steps. Stable per device+tile. */
const PercentRule = z.object({
  kind: z.literal('percent'),
  pct: z.number().int().min(0).max(100).multipleOf(5),
});

/** Share that grows `perDay` points per day from the pilot's `from` date
 *  until it reaches 100% — a gradual ramp. Requires `from` (the anchor). */
const PercentRampRule = z.object({
  kind: z.literal('percentRamp'),
  startPct: z.number().int().min(0).max(100).multipleOf(5),
  perDay: z.number().int().min(1).max(100),
});

/**
 * Devices whose own LAN IPv4 (self-reported) falls in any listed subnet. An
 * empty list is allowed — it's the in-progress state while an admin is choosing
 * subnets in the Builder (or has unchecked them all). It simply matches
 * nobody (the engine fail-closes on an empty list), so editing never leaves
 * the whole config invalid.
 */
const SubnetRule = z.object({
  kind: z.literal('subnet'),
  cidrs: z.array(Cidr).max(50),
});

/**
 * Devices carrying any of the listed customer-assigned tags. An empty list is
 * allowed — the in-progress state while an admin is still choosing tags. It
 * matches nobody (the engine fail-closes), so editing never invalidates the
 * whole config.
 */
const TagRule = z.object({
  kind: z.literal('tag'),
  tags: z.array(z.string().min(1).max(60)).max(50),
});

export const PilotRule = z.discriminatedUnion('kind', [
  PercentRule,
  PercentRampRule,
  SubnetRule,
  TagRule,
]);
export type PilotRule = z.infer<typeof PilotRule>;

export const Pilot = z
  .object({
    from: Iso8601.optional(),
    to: Iso8601.optional(),
    rule: PilotRule,
  })
  .superRefine((p, ctx) => {
    // A ramp has nothing to grow from without a start anchor.
    if (p.rule.kind === 'percentRamp' && p.from === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'a percentRamp pilot requires a `from` start date',
        path: ['from'],
      });
    }
    if (p.from !== undefined && p.to !== undefined && Date.parse(p.to) <= Date.parse(p.from)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '`to` must be after `from`',
        path: ['to'],
      });
    }
  });
export type Pilot = z.infer<typeof Pilot>;
